import { useState, useEffect } from 'react';
import { toast } from 'sonner';

import {
    draftFromUser,
    draftToPatch,
    isDraftComplete,
    ProfessionalProfileFields,
    type IProfessionalProfileDraft,
} from '@/components/profile/professional-profile-fields';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser, useUpdateCurrentUser, useUpdateProfile } from '@/hooks/use-current-user';

export function ProfilePage() {
    const { data: user, isLoading } = useCurrentUser();
    const updateUser = useUpdateCurrentUser();
    const updateProfile = useUpdateProfile();
    const [name, setName] = useState('');
    // Derived from the user row until the operator edits it; once they do, the
    // edit is kept even across a refetch. Seeding through a `useEffect` instead
    // would mount the professional-profile Select fields with an empty value
    // that transitions to the real one right after mount, inside a <form> that
    // trips a Radix Select bubble-select quirk and drops the seeded role,
    // specialty and country back to empty.
    const [editedDraft, setEditedDraft] = useState<IProfessionalProfileDraft | null>(null);
    const draft = editedDraft ?? draftFromUser(user);

    useEffect(() => {
        if (user?.name) {
            setName(user.name);
        }
    }, [user?.name]);

    if (isLoading) {
        return <ProfileSkeleton />;
    }

    const saveProfessionalProfile = async (event: React.FormEvent) => {
        event.preventDefault();
        try {
            await updateProfile.mutateAsync(draftToPatch(draft));
            toast.success('Professional profile updated');
        } catch {
            toast.error('Failed to update your professional profile');
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        updateUser.mutate(
            { name },
            {
                onSuccess: () => {
                    toast.success('Profile updated successfully');
                },
                onError: () => {
                    toast.error('Failed to update profile');
                },
            }
        );
    };

    const initials = (user?.name ?? user?.email ?? 'U')
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Profile Settings</h1>
                <p className="text-muted-foreground">Manage your account settings</p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Profile Information</CardTitle>
                    <CardDescription>Update your personal information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex items-center gap-4">
                        <Avatar className="h-20 w-20">
                            <AvatarImage src={user?.avatarUrl ?? undefined} />
                            <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                        </Avatar>
                        <div>
                            <p className="font-medium">{user?.name ?? 'No name set'}</p>
                            <p className="text-sm text-muted-foreground">{user?.email}</p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" value={user?.email ?? ''} disabled />
                            <p className="text-xs text-muted-foreground">Email cannot be changed here</p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="name">Display Name</Label>
                            <Input
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Enter your name"
                            />
                        </div>

                        <Button type="submit" disabled={updateUser.isPending}>
                            {updateUser.isPending ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Professional profile</CardTitle>
                    <CardDescription>
                        Appears on exported reports and in the audit trail. Required before first clinical
                        use.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={(event) => void saveProfessionalProfile(event)} className="space-y-4">
                        <ProfessionalProfileFields
                            value={draft}
                            onChange={setEditedDraft}
                            idPrefix="settings"
                            disabled={updateProfile.isPending}
                        />
                        <Button type="submit" disabled={updateProfile.isPending || !isDraftComplete(draft)}>
                            {updateProfile.isPending ? 'Saving...' : 'Save professional profile'}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Account Details</CardTitle>
                    <CardDescription>Your account information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <p className="text-muted-foreground">User ID</p>
                            <p className="font-mono">{user?.id}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground">Member Since</p>
                            <p>{user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function ProfileSkeleton() {
    return (
        <div className="mx-auto max-w-2xl space-y-6">
            <div>
                <Skeleton className="h-8 w-48" />
                <Skeleton className="mt-2 h-4 w-64" />
            </div>
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-40" />
                    <Skeleton className="h-4 w-56" />
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex items-center gap-4">
                        <Skeleton className="h-20 w-20 rounded-full" />
                        <div>
                            <Skeleton className="h-5 w-32" />
                            <Skeleton className="mt-1 h-4 w-48" />
                        </div>
                    </div>
                    <div className="space-y-4">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-24" />
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
